import React from "react";
import ReactDOM from "react-dom";
import { StyleSheetManager } from "styled-components";
import { FeedbackProps, ThemeProvider } from "@tomis-tech/chat-ui";

import { initialize } from "@/services/initialize";
import { trackEvent, initializeTracking } from "@/tracking";
import { startAnalytics, setWidgetMounted } from "@/analytics";
import { injectStylesShadowRoot, WIDGET_ROOT_ATTR } from "@/styles";

import pkg from "../package.json";
import { App } from "./App.tsx";
import { bindChatApiLoad, getChatApi } from "./chatApi.ts";
import {
  confirmMount,
  hasEmbedMount,
  hasFloatingMount,
  mountCount,
  releaseMount,
  resolveEmbedSelector,
  tryReserveMount,
} from "./instances.ts";
import { chatLogPrefix, getChatDomIds, getNamespace } from "./namespace.ts";
import { getChatSession } from "./session/index.ts";
import {
  ChatWidgetConfiguration,
  ChatWidgetLoadResponse,
  ChatWidgetUserIdentity,
} from "./types.ts";

/** Light-DOM host id: floating is `#${ns}-chat-widget`; embed prefers `#${ns}-chat-embed`. */
function getHostId(embed: boolean): string {
  const ids = getChatDomIds();
  return embed ? ids.embed : ids.host;
}

/**
 * Host pages sometimes hide empty elements (`div:empty { display: none }`).
 * Shadow tree contents do not count as children for `:empty`, so the light-DOM
 * host looks empty unless we add a real child.
 *
 * `window[namespace].chat.hide()` still works — it hides the React tree inside
 * the shadow root, not this host.
 */
function hardenHostElement(root: HTMLElement) {
  const { versionAttr, hostMarkerAttr } = getChatDomIds();
  root.setAttribute(versionAttr, pkg.version);

  if (!root.querySelector(`[${hostMarkerAttr}]`)) {
    const marker = document.createElement("span");
    marker.setAttribute(hostMarkerAttr, "");
    marker.setAttribute("aria-hidden", "true");
    marker.style.cssText =
      "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;";
    root.appendChild(marker);
  }
}

/** Create chat widget in Shadow Root.
 *
 * This function will create a Shadow Root and attach it to the document body.
 * It will then render the chat widget inside the Shadow Root.
 * We are using a Shadow Root to prevent the chat widget from interfering with
 * the styles of the host page. This requires a small work around to make
 * `styled-components` attach it's stylesheet inside the Shadow Root, which you can see below.
 *
 * @returns A promise that resolves when the widget is ready to use.
 */
async function createWidget(
  config: ChatWidgetConfiguration,
  /** If `true` render chat widget in element */
  embed: boolean,
  feedback: FeedbackProps["mode"],
  /** If `true` mark conversations as test_mode in database. If true, prevents product analytics */
  test_mode: boolean,
  /** Initial conversation history if loading existing conversation */
  conversationHistory?: ChatWidgetLoadResponse["conversation_history"],
  /** Live-chat handoff state for a restored conversation */
  handoffStatus?: ChatWidgetLoadResponse["handoff_status"],
  /** Initial conversation-level forms from load */
  forms?: ChatWidgetLoadResponse["forms"],
  /** Required form configuration for form-only mode */
  requiredForm?: ChatWidgetLoadResponse["required_form"],
  /** CSS selector for the embed host (default `#${ns}-chat-embed`) */
  container?: string,
): Promise<any> {
  const hostId = getHostId(embed);
  let root: HTMLElement;

  if (embed) {
    const selector = resolveEmbedSelector(container);
    const embeddedElement = document.querySelector(selector);
    if (!(embeddedElement instanceof HTMLElement)) {
      throw new Error(
        `Element matching '${selector}' not found. Please add a container to your page.`,
      );
    }
    if (embeddedElement.shadowRoot) {
      throw new Error(
        `Element matching '${selector}' already hosts a chat widget.`,
      );
    }
    console.warn(
      `${chatLogPrefix()} Rendering chat widget in embedded mode: `,
      embeddedElement,
    );
    root = embeddedElement;
    if (!root.id) {
      root.id = hostId;
    }
  } else {
    const existing = document.getElementById(hostId);
    if (existing?.shadowRoot) {
      throw new Error(
        `Element with id '${hostId}' already hosts a chat widget.`,
      );
    }
    root = existing ?? document.createElement("div");
    root.id = hostId;
    if (!root.parentElement) {
      document.body.appendChild(root);
    }
  }

  hardenHostElement(root);

  const shadow = root.attachShadow({ mode: "open" });
  const styleSlot = document.createElement("section");
  styleSlot.setAttribute(WIDGET_ROOT_ATTR, "");
  shadow.appendChild(styleSlot);
  const renderIn = document.createElement("div");
  renderIn.id = getChatDomIds().app;
  styleSlot.appendChild(renderIn);

  // Make sure the chat widget takes up the full size of embed element
  if (embed) {
    styleSlot.setAttribute("style", "height: 100%; width: 100%;");
    renderIn.setAttribute("style", "height: 100%; width: 100%;");
  }

  // Apply CSS styles inside Shadow Root
  injectStylesShadowRoot(shadow);

  return new Promise((resolve) => {
    ReactDOM.render(
      <React.StrictMode>
        <StyleSheetManager target={styleSlot}>
          <ThemeProvider
            primaryColor={config.color}
            styleSheetTarget={styleSlot}
            classPrefix={getNamespace()}
          >
            <App
              config={config}
              onReady={() => resolve(null)}
              feedback={feedback}
              embed={embed}
              test_mode={test_mode}
              conversationHistory={conversationHistory}
              handoffStatus={handoffStatus}
              forms={forms}
              requiredForm={requiredForm}
            />
          </ThemeProvider>
        </StyleSheetManager>
      </React.StrictMode>,
      renderIn,
    );
  });
}

/** This function is the entry point for JavaScript loaded on the client site.
 *
 * 1. Fetch API to get configuration & validate bot for this domain
 * 2. If valid, create iframe and append to DOM. If invalid, log to console and do nothing.
 * 3. Setup tracking
 *
 * @returns Promise that resolves when React chat widget has been created and
 * the chat API has been initialized */
export async function load({
  id,
  settings = {},
  embed = false,
  container,
  feedback = "disabled",
  test_mode = false,
  user,
  form_key,
  preview = false,
}: {
  /** TOMIS site-slug */
  id: string;
  /** Chat widget configuration for this particular page.
   *
   * Any settings provided here will override settings from the database.
   * This give us granular control over the chat widget on a per-page basis,
   * as well as helps with testing.
   */
  settings?: Partial<ChatWidgetConfiguration>;
  /** If true, chat widget will be embedded in the page instead of a floating dialog */
  embed?: boolean;
  /** CSS selector for the embed host. Defaults to `#${namespace}-chat-embed`, with a fallback to `#${namespace}-chat-widget` for legacy embed-only snippets. */
  container?: string;
  /** Feedback mode for chat widget */
  feedback?: FeedbackProps["mode"];
  test_mode?: boolean;
  /** Optional end-user identity to send to /chat-widget/load */
  user?: ChatWidgetUserIdentity;
  /** Optional form key to request form-only mode.
   *
   * When provided, the backend will validate if this form is available for the site.
   * If valid, the widget will render in form-only mode with just that form.
   * If invalid, the form_key is ignored and normal chat mode is used.
   *
   * Example: `form_key: "handoff_sms"` for SMS lead capture form
   */
  form_key?: string;
  /** Preview mode — the form renders but submissions are no-ops.
   * Used for Twilio verification screenshots. */
  preview?: boolean;
}) {
  const kind = embed ? "embed" : "floating";
  const embedSelector = embed ? resolveEmbedSelector(container) : null;
  const reserved = tryReserveMount(kind, id, embedSelector);
  if (!reserved.ok) {
    console.warn(reserved.message);
    return;
  }

  const isFirstMount = mountCount() === 1;

  if (form_key && !isFirstMount) {
    releaseMount(kind);
    console.warn(
      `${chatLogPrefix()} Form-only and chat cannot run together. Skip form_key on the second load().`,
    );
    return;
  }

  if (isFirstMount) {
    getChatApi().chat.preview = preview;
    getChatApi().chat.test_mode = test_mode;
    getChatApi().chat.feedback = feedback;
    initializeTracking();
  }

  try {
    const resolvedSettings: Partial<ChatWidgetConfiguration> = {
      ...settings,
    };

    const results = await initialize(
      id,
      resolvedSettings,
      user,
      form_key,
      preview,
    );

    if (!results.success || !results.config) {
      releaseMount(kind);
      startAnalytics(id, { loadSucceeded: false });
      return trackEvent("load_prevented", {
        error: results.errorMessage,
        embed,
      });
    }

    if (results.config?.disabled) {
      releaseMount(kind);
      startAnalytics(id, {
        loadSucceeded: true,
        disabled: true,
        trackingEnabled: results.trackingEnabled,
      });
      console.warn(
        `${chatLogPrefix()} Chat widget is disabled for "${id}". Please check your configuration.`,
      );
      return trackEvent("load_prevented", {
        error: "Chat widget is disabled",
        embed,
      });
    }

    if (results.requiredForm && !isFirstMount) {
      releaseMount(kind);
      console.warn(
        `${chatLogPrefix()} Form-only mode cannot share the page with a second chat widget.`,
      );
      return;
    }

    startAnalytics(id, {
      loadSucceeded: true,
      disabled: false,
      trackingEnabled: results.trackingEnabled,
    });

    await createWidget(
      results.config,
      embed,
      feedback,
      test_mode,
      results.conversationHistory,
      results.handoffStatus,
      results.forms,
      results.requiredForm,
      embedSelector ?? undefined,
    );
    confirmMount(kind);

    syncEmbedFlag();
    setWidgetMounted(true);

    trackEvent("loaded", {
      ...results.config,
      embed,
      form_only_mode: Boolean(results.requiredForm),
      required_form_key: results.requiredForm?.key,
    });

    const conversationStarted =
      getChatSession(id)?.getState().hasRealConversation ||
      (results.conversationHistory?.length ?? 0) > 0;

    const hasAnnouncement = (results.config.announcements?.length ?? 0) > 0;

    if (
      !embed &&
      results.config.popupMessages &&
      !results.requiredForm &&
      !conversationStarted &&
      !hasAnnouncement
    ) {
      showProactiveMessages(
        results.config.popupMessages,
        results.config.popupMessagesDelay,
      );
    }
  } catch (error) {
    releaseMount(kind);
    syncEmbedFlag();
    startAnalytics(id, { loadSucceeded: false });
    trackEvent("load_failed", {
      error: error instanceof Error ? error.message : "Unknown error",
      embed,
    });
    throw error;
  }
}

function syncEmbedFlag() {
  const chat = getChatApi().chat;
  if (hasEmbedMount() && hasFloatingMount()) {
    chat.embed = undefined;
  } else {
    chat.embed = hasEmbedMount();
  }
}

/** If chat widget hasn't been opened yet, show proactive "welcome" messages */
function showProactiveMessages(messages: string[] = [], delay: number = 0) {
  const chat = getChatApi().chat;
  chat.proactive.clear();
  setTimeout(() => {
    chat.proactive.push(...messages);
  }, delay * 1000);
}

bindChatApiLoad(load);
